import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Heart, Lock, ShoppingCart } from '@phosphor-icons/react';
import { Icon } from '../Icon';
/* Deep import, against the barrel convention, ON PURPOSE: `complex/index.js`
   pulls in `Toolbar`, which imports `SearchClear` back out of this same shells
   folder. Going through the barrel would put the whole desktop component set in
   a cycle with the chrome that mounts on every page. The file is the leaf. */
import { Tooltip } from '../desktop/complex/Tooltip';
import { cn } from '../../lib/cn';
import { MARKET_NAV, isNavItemActive } from './navModel';
import { SearchBox } from './SearchBox';
import { ThemeToggle } from './ThemeToggle';
import { UserMenu } from './UserMenu';
import { useCart } from '../../../Contexts/CartContext';
import { useAuth } from '../../../hooks/useAuth';
import { routes } from '../../../routes/routes';
import { Wordmark } from '../../brand';

/**
 * MarketShell top nav, desktop only.
 *
 * Order is the doctrine, not a layout accident: wordmark, then the global
 * search taking as much of its 560px as it can get, then the section links,
 * then the live-state icons, then identity. Search sits second because on a
 * buying surface it is the front door, and `.dsk-nav-icons` pushes the trailing
 * cluster right with `margin-left: auto`.
 *
 * FILL IS ACTIVE. The cart glyph fills when the cart is holding something and
 * the heart fills when the collection is, because that is live state. Neither
 * fills on hover.
 *
 * The props still say `wishlist*`; every LABEL says Collection. The app had
 * three words for one list and Collection is the one it keeps, but the prop
 * names are threaded through `MarketShell`, `TabBar` and four pages, so they
 * stay as they are rather than being renamed for a word nobody reads.
 *
 * The `checkout` variant strips the nav to a wordmark and a reassurance line.
 * A focused flow should offer no way to wander off mid payment, so the links,
 * the search and the cart all go.
 *
 * @param {{
 *   variant?: 'default'|'checkout',
 *   links?: Array<object>,
 *   search?: { value?: string, onChange?: Function, onSubmit?: Function, placeholder?: string, suggestions?: Array<object>, onSelectSuggestion?: Function },
 *   showSearch?: boolean,
 *   wishlistCount?: number,
 *   wishlistTo?: string,
 *   onCartClick?: () => void,
 *   activeNav?: string,
 *   className?: string
 * }} props
 */
export function TopNav({
  variant = 'default',
  links = MARKET_NAV,
  search,
  showSearch = true,
  wishlistCount = 0,
  wishlistTo = routes.COLLECTION,
  onCartClick,
  activeNav,
  className,
}) {
  const { pathname } = useLocation();
  const cart = useCart();
  const cartCount = cart?.itemCount || 0;
  const { isAuthenticated } = useAuth();

  if (variant === 'checkout') {
    return (
      <header className={cn('dsk-topnav', className)}>
        <Wordmark className="dsk-wordmark" />
        <div className="dsk-nav-icons">
          <p className="dsk-topnav__secure">
            <Icon as={Lock} size="sm" />
            Secure checkout
          </p>
          <ThemeToggle track="desktop" />
        </div>
      </header>
    );
  }

  return (
    <header className={cn('dsk-topnav', className)}>
      <Wordmark className="dsk-wordmark" />

      {showSearch && (
        <SearchBox
          value={search?.value}
          onChange={search?.onChange}
          onSubmit={search?.onSubmit}
          placeholder={search?.placeholder}
          suggestions={search?.suggestions}
          sections={search?.sections}
          emptyState={search?.emptyState}
          onSelectSuggestion={search?.onSelectSuggestion}
          onRemoveSuggestion={search?.onRemoveSuggestion}
          openOnFocus={search?.openOnFocus}
          label="Search the marketplace"
        />
      )}

      <nav className="dsk-navlinks" aria-label="Primary">
        {/* `authOnly` items are dropped for a signed out visitor. Orders and
            Dashboard mean nothing without an account, and a nav link whose
            only outcome is a redirect to /login is a dead end. */}
        {links
          .filter((item) => !item.authOnly || isAuthenticated)
          .map((item) => {
            const active = activeNav ? activeNav === item.id : isNavItemActive(item, pathname);
            return (
              <Link
                key={item.id}
                to={item.to}
                className={cn('dsk-navlink', active && 'is-active')}
                aria-current={active ? 'page' : undefined}
              >
                {item.label}
              </Link>
            );
          })}
      </nav>

      {/* EVERY ICON IN THIS CLUSTER CARRIES A TOOLTIP, and that is the whole
          reason they are allowed to be icons at all. Four unlabelled glyphs in
          a row is the one place in the app where a control says nothing until
          you click it, and "what does the heart do" is a question a buyer
          should never have to answer by pressing it and finding out.

          The library `Tooltip` and not a `title` attribute: `title` waits about
          a second, cannot be styled, never appears on keyboard focus, and is
          announced on top of the `aria-label` so a screen reader hears the same
          words twice. `Tooltip` shows on hover AND focus, portals out so the
          sticky header cannot clip it, and wires `aria-describedby`. */}
      <div className="dsk-nav-icons">
        {/* `ThemeToggle` is a plain function component, so `Tooltip` cannot put
            its ref on it and gets a wrapper to measure instead. `inline-flex`
            keeps it a single flex item exactly the size of the button, so
            `.dsk-nav-icons`' gap is unchanged — a bare `<span>` would be
            inline and reintroduce line-height space around the button, and
            `display: contents` measures as a zero rect and pins the tip to the
            top left corner of the window. */}
        <Tooltip value="Switch theme" placement="bottom">
          <span style={{ display: 'inline-flex' }}>
            <ThemeToggle track="desktop" />
          </span>
        </Tooltip>

        {/* THE HEART IS A DESTINATION. It went to `/profile` because there was
            no collection page, so the one control on the page that means "the
            cards I saved" dropped the buyer on their account settings with the
            saved cards four rows down — the single most reported piece of
            wrongness in this nav. `routes.COLLECTION` is a real page now.

            The tip says "Go to Collection" and not "Collection" because the
            heart is the same glyph that SAVES a card everywhere else in the
            app; the verb is what separates the two meanings. */}
        <Tooltip value="Go to Collection" placement="bottom">
          <Link
            to={wishlistTo}
            className="dsk-iconbtn"
            aria-label={
              wishlistCount > 0 ? `Collection, ${wishlistCount} saved` : 'Collection'
            }
          >
            <Icon as={Heart} active={wishlistCount > 0} />
          </Link>
        </Tooltip>

        {/* THE CART OPENS THE DRAWER. It does not navigate.
            This was a `<Link to={routes.CHECKOUT}>` whenever no `onCartClick`
            was passed — which is nearly every page — so clicking the cart on
            desktop skipped the cart AND the review step and dropped the buyer
            straight into checkout. The mobile header was fixed earlier; this is
            the same bug on the other track. `onCartClick` still wins when a
            page wants different behaviour. */}
        {/* "Open cart", not "Cart": it opens a drawer over this page rather
            than going anywhere, and the tip is the one chance to say so before
            the click. */}
        <Tooltip value="Open cart" placement="bottom">
          <button
            type="button"
            className="dsk-iconbtn"
            onClick={onCartClick || (() => cart?.setShowCartOverlay?.(true))}
            aria-label={cartCount > 0 ? `Cart, ${cartCount} items` : 'Cart'}
            aria-haspopup="dialog"
          >
            <Icon as={ShoppingCart} active={cartCount > 0} />
            {cartCount > 0 && <span className="dsk-cart-badge">{cartCount}</span>}
          </button>
        </Tooltip>

        <UserMenu />
      </div>
    </header>
  );
}

export default TopNav;
